import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, Navigate, Outlet, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import AssetForm from '../AssetForm';
import AssetCard from '../AssetCard';
import CompanyChip from '../asset-company/CompanyChip';
import { useConfirm } from '../ConfirmDialog';
import ActionDialog from './ActionDialog';
import PluggedNotice from './PluggedNotice';
import { MakeDeskDialog, PutOnDeskDialog } from './DeskDialogs';
import { contractorAuth } from '../../lib/contractorAuth';
import { useMe } from '../../lib/me';
import {
  useAssetMeta, deviceName, LIFECYCLE_LABEL, OPERATIONAL_LABEL, lifecycleTone, operationalTone,
  holderLabel, holderKeyOf
} from '../../lib/assets';
import TagChip from './TagChip';
import { roleOf, tabsFor, tabFromPath, reporting, signedInDiffers } from './assetView';
import '../Assets.css';
import './AssetLayout.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * ONE DEVICE PAGE FOR EVERY ROLE (October 2026 redesign, approved by Gordon
 * on the 27th), modelled on the ticket page's shell (components/ticket/
 * TicketLayout.js).
 *
 * A sticky header - tag and name, category, company, the lifecycle and
 * working-state chips, "With <holder> · Kept at <place>", the reporting
 * dot, and the one calm row of actions - over a row of tabs that are real
 * addresses:
 *
 *   /assets/:id            Overview      /assets/:id/device    Device
 *   /assets/:id/history    History       /assets/:id/notes     Notes
 *   /assets/:id/tickets    Tickets       /assets/:id/files     Files
 *                                        /assets/:id/access    Access (admins)
 *
 * The same component serves staff, administrators and the contractor
 * portal (pages/ContractorDashboard.js draws it under the portal's strip);
 * the role decides the tabs and the actions (assetView.js). Somebody
 * without register access who followed the card on their ticket here gets
 * that card and nothing more - the server sends nothing else.
 *
 * Every write is still the server's to allow.
 */
export default function AssetLayout() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const meta = useAssetMeta();
  const me = useMe();
  // A contractor looking through a grant (062): their portal token signs
  // every call, and the server trims what they get.
  const portal = Boolean(contractorAuth()) && !me?.signedIn;
  const base = `/assets/${id}`;
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [doing, setDoing] = useState(null);
  const [notes, setNotes] = useState(null);
  const [counts, setCounts] = useState({});
  const [problem, setProblem] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirm, confirmDialog] = useConfirm();
  // Arrived by scanning the label: IT's walk-round, where the page they land
  // on IS the check that the sticker is on the right machine.
  const [search] = useSearchParams();
  const scanned = search.get('scanned') === '1';
  // Arrived from the Devices list to finish a record its agent made:
  // straight into Edit, with the holder it suggests (?holder=).
  const wantEdit = search.get('edit') === '1';
  useEffect(() => { if (wantEdit && data?.access === 'write') setDoing('edit'); }, [wantEdit, data?.access]);

  const load = useCallback(() => axios.get(`${API_BASE}/assets/${id}`, contractorAuth())
    .then((r) => { setData(r.data); setError(null); setCounts({}); })
    .catch((e) => setError({
      status: e.response?.status,
      text: e.response?.data?.error || 'Could not load this device.'
    })), [id]);
  useEffect(() => { load(); }, [load]);

  /*
   * The notes (094), loaded once with the page: the tab shows them, the
   * Overview previews the latest, and their number sits on the tab. A
   * server without the table answers 503, which the tab explains.
   */
  const reloadNotes = useCallback(async () => {
    try {
      const r = await axios.get(`${API_BASE}/assets/${id}/notes`, contractorAuth());
      setNotes(r.data && typeof r.data === 'object' ? r.data : { notes: [] });
    } catch (e) {
      setNotes({ status: e.response?.status || 0, notes: [], error: e.response?.data?.error });
    }
  }, [id]);
  useEffect(() => { if (data?.access) reloadNotes(); }, [data?.access, reloadNotes]);

  // Unlink: this device, or one plugged into it. The server checks write
  // on the device being unlinked, which is the child's, not this page's.
  const [unlinkError, setUnlinkError] = useState('');
  const unlinkDevice = async (childId) => {
    setUnlinkError('');
    try {
      await axios.post(`${API_BASE}/assets/${childId}/unlink`, {}, contractorAuth());
      load();
    } catch (e) {
      setUnlinkError(e.response?.data?.error || 'Could not unlink it.');
    }
  };

  /* --------------------------------------------- the compact header */
  // The ticket page's two guards against a blinking header (26 Oct): a gap
  // between the line in (140px) and the line out (40px), and a spacer that
  // keeps the page the same height while the header is small.
  const headRef = useRef(null);
  const fullHead = useRef(0);
  const [compact, setCompact] = useState(false);
  const [spare, setSpare] = useState(0);
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const y = window.scrollY;
        const head = headRef.current;
        if (head && !head.classList.contains('as-head-compact')) fullHead.current = head.offsetHeight;
        setCompact((was) => (was ? y > 40 : y > 140));
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); cancelAnimationFrame(raf); };
  }, []);
  useLayoutEffect(() => {
    const el = headRef.current;
    if (!el) return;
    if (!compact) { fullHead.current = el.offsetHeight; setSpare(0); return; }
    setSpare(Math.max(0, fullHead.current - el.offsetHeight));
  }, [compact]);

  /* ------------------------------------------------- the make-holder line */
  const [confirmingHolder, setConfirmingHolder] = useState(false);
  const makeHolder = async (userId) => {
    setProblem('');
    try {
      await axios.post(`${API_BASE}/assets/${id}/assign`,
        { user_id: userId, reason: 'They are signed in to it, by its agent’s report' }, contractorAuth());
      setConfirmingHolder(false);
      load();
    } catch (e) {
      setProblem(e.response?.data?.error || 'That did not work.');
    }
  };

  const role = roleOf({ data, meta, portal });
  const tabs = useMemo(() => tabsFor(role), [role]);
  const setCount = useCallback((key, n) => setCounts((c) => (c[key] === n ? c : { ...c, [key]: n })), []);

  /* ------------------------------------------------------ rendering */
  if (error) {
    return (
      <div className="container as-page">
        <p><Link to="/assets">← Assets</Link></p>
        <p className={error.status === 503 ? 'as-note' : 'as-error'} role="alert">
          {error.status === 404 ? 'There is no such device, or it is not one you may see.' : error.text}
        </p>
      </div>
    );
  }
  if (!data) return <div className="container as-page"><p className="as-quiet">Loading…</p></div>;

  // Here from a ticket, without register access: the card is all there is.
  if (!data.access) {
    return (
      <div className="container as-page as-card-only">
        <div className="page-header"><h1 className="page-title">{deviceName(data.card)}</h1></div>
        <AssetCard asset={data.card} />
        <button type="button" className="btn btn-primary"
          onClick={() => navigate(`/create?asset=${data.card.id}`)}>
          Report a problem with it
        </button>
      </div>
    );
  }

  // A tab this person does not get (an old link, or a role that changed):
  // the Overview, rather than an empty frame.
  const here = tabFromPath(location.pathname);
  if (here !== 'overview' && !tabs.some((t) => t.key === here)) {
    return <Navigate to={base} replace />;
  }

  const a = data.asset;
  const write = data.access === 'write';
  const admin = role === 'admin';
  const gone = ['retired', 'disposed'].includes(a.lifecycle_status);
  const dot = reporting(a, data.device);
  // Not in the portal: a contractor is not the one to hand a firm device to a member of staff.
  const differs = write && !portal && data.device ? signedInDiffers(a, meta) : null;
  const done = () => { setDoing(null); load(); if (doing === 'note') reloadNotes(); };
  const enrolled = Boolean(data.device?.enrolment);

  /* The actions: one calm row, the rest behind More. */
  const canReport = !gone && meta?.enabled;
  const give = write && !gone ? { key: 'assign', label: holderKeyOf(a) ? 'Give to someone else' : 'Give to someone' } : null;
  const take = write && !gone && holderKeyOf(a) ? { key: 'return', label: 'Take back' } : null;
  const menu = [];
  if (give) menu.push({ ...give, phone: true, run: () => setDoing('assign') });
  if (take) menu.push({ ...take, phone: true, run: () => setDoing('return') });
  if (write && !gone) {
    menu.push({ key: 'move', label: 'Move…', run: () => setDoing('move') });
    menu.push({ key: 'status', label: 'Change status…', run: () => setDoing('status') });
    menu.push({ key: 'edit', label: 'Edit…', run: () => setDoing('edit') });
    // Personal items (105): outside the firm's register, no tag, still held and placed.
    const makePersonal = !a.is_personal;
    menu.push({ key: 'personal', label: makePersonal ? 'Mark as personal…' : 'Make it the firm’s…', run: async () => {
      const ok = await confirm(makePersonal
        ? { title: 'Mark as personal?', message: 'It loses its tag and leaves the company list. It stays with its holder and where it is.', confirmLabel: 'Mark as personal' }
        : { title: 'Make it the firm’s?', message: 'It gets a new tag and goes back into the company list.', confirmLabel: 'Make it the firm’s' });
      if (!ok) return;
      setProblem('');
      try { await axios.post(`${API_BASE}/assets/${id}/personal`, { personal: makePersonal }, contractorAuth()); load(); } catch (e) { setProblem(e.response?.data?.error || 'That did not work.'); }
    } });
  }
  // Desks (108): a laptop or desktop's page makes the desk it stands at;
  // any device may be put on one. Only once the server has desks.
  if (write && !gone && meta?.desks) {
    if (/laptop|desktop/i.test(a.category_name || '')) menu.push({ key: 'make-desk', label: 'Make this a desk…', run: () => setDoing('make-desk') });
    menu.push({ key: 'put-on-desk', label: 'Put on a desk…', run: () => setDoing('put-on-desk') });
  }
  if (write || portal) menu.push({ key: 'note', label: 'Add a note…', run: () => setDoing('note') });
  // Lost mode is any writer's; stopping the agent an administrator's - the
  // one rule here and on the Device tab.
  if (write && enrolled && !a.lost_at) menu.push({ key: 'lost', label: 'Mark lost…', run: () => setDoing('lost') });
  if (write && a.lost_at) {
    menu.push({ key: 'found', label: 'Found it', run: async () => {
      setProblem('');
      try { await axios.post(`${API_BASE}/assets/${id}/found`, {}, contractorAuth()); load(); } catch (e) { setProblem(e.response?.data?.error || 'That did not work.'); }
    } });
  }
  if (admin && enrolled) {
    menu.push({ key: 'stop-agent', label: 'Stop its agent…', run: async () => {
      if (!(await confirm({ title: 'Stop its agent?', message: 'The device stops reporting in. For a machine that has been wiped or sold.', confirmLabel: 'Stop it', danger: true }))) return;
      setProblem('');
      try {
        await axios.delete(`${API_BASE}/assets/${id}/enrolment`, { data: { reason: 'Stopped from its page' } });
        load();
      } catch (e) { setProblem(e.response?.data?.error || 'That did not work.'); }
    } });
  }
  if (write && !portal) menu.push({ key: 'label', label: 'Print / reprint label', run: () => navigate(`/assets/labels?ids=${a.id}`) });
  if (write && !gone) menu.push({ key: 'retire', label: 'Retire…', danger: true, run: () => setDoing('retire') });

  const count = (key) => {
    if (key in counts) return counts[key];
    if (key === 'history') return (data.events || []).length;
    if (key === 'tickets') return (data.tickets || []).length;
    if (key === 'notes') return notes && notes.status !== 503 ? (notes.notes || []).length : (typeof data.notes_count === 'number' ? data.notes_count : null);
    if (key === 'files') return typeof data.documents_count === 'number' ? data.documents_count : null;
    if (key === 'access') return (data.grants || []).length;
    return null;
  };

  const ctx = {
    data, asset: a, write, admin, portal, role, gone, meta, me, base, scanned, notes, reloadNotes,
    reload: load, setCount, unlinkDevice, unlinkError, openAction: setDoing
  };
  const subline = [a.category_name, a.serial_number ? `Serial ${a.serial_number}` : null,
    a.is_shared ? 'Shared equipment' : null].filter(Boolean).join(' · ');

  return (
    <div className="as-page" data-role={role}>
      <header ref={headRef} className={`as-head${compact ? ' as-head-compact' : ''}`}>
        <div className="as-head-in">
          <div className="as-crumbs">
            <Link className="as-back" to="/assets"><span aria-hidden="true">←</span> Assets</Link>
            <span className="as-sep" aria-hidden="true">/</span>
            <TagChip asset={a} />
            {gone && <span className="as-chip" data-tone="idle">{LIFECYCLE_LABEL[a.lifecycle_status]} — view only</span>}
          </div>

          <div className="as-titlerow">
            <div className="as-titles">
              <h1 className="as-title">{deviceName(a)}</h1>
              {subline && <p className="as-subline as-quiet">{subline}</p>}
            </div>
            <div className="as-head-actions">
              {canReport && (
                <button type="button" className="btn btn-primary" data-action="report"
                  onClick={() => navigate(`/create?asset=${a.id}`)}>
                  Report a problem
                </button>
              )}
              {give && (
                <button type="button" className="btn btn-secondary as-phone-hide" data-action="assign" onClick={() => setDoing('assign')}>
                  {give.label}
                </button>
              )}
              {take && (
                <button type="button" className="btn btn-secondary as-phone-hide" data-action="return" onClick={() => setDoing('return')}>
                  {take.label}
                </button>
              )}
              {menu.length > 0 && <MoreMenu items={menu} open={menuOpen} setOpen={setMenuOpen} />}
            </div>
          </div>

          <div className="as-chips">
            {a.is_personal && <span className="as-chip" data-tone="info" data-testid="personal-chip">Personal</span>}
            <span className="as-chip" data-tone={lifecycleTone(a.lifecycle_status)}>
              {LIFECYCLE_LABEL[a.lifecycle_status]}
            </span>
            {a.operational_status !== 'operational' && (
              <span className="as-chip" data-tone={operationalTone(a.operational_status)}>
                {OPERATIONAL_LABEL[a.operational_status]}
              </span>
            )}
            <CompanyChip name={a.company_name} color={a.company_color} code={a.company_code} />
            <span className="as-whereline" data-testid="whereline">
              {`${a.desk_id && !a.holder_name ? 'Held by' : 'With'} ${holderLabel(a) || 'nobody'} · Kept at ${a.location_path || 'no recorded place'}`}
            </span>
            {a.desk_id && !portal && (
              <Link className="as-chip" data-tone="info" to={`/assets/desks/${a.desk_id}`} data-testid="desk-link">Desk page</Link>
            )}
            {dot && (
              <span className="as-reporting" data-tone={dot.tone} data-testid="reporting">
                <span className="as-dot" aria-hidden="true" />{dot.text}
              </span>
            )}
          </div>

          {differs && differs.name && (
            <div className="as-signedin" data-testid="signed-in-line">
              {!confirmingHolder ? (
                <>
                  <span>{`${differs.name} is signed in to it — ${differs.sentence}.`}</span>
                  <button type="button" className="btn btn-secondary btn-small" onClick={() => setConfirmingHolder(true)}>
                    {`Make ${differs.name} the holder`}
                  </button>
                </>
              ) : (
                <span className="as-inline-form" data-testid="make-holder-confirm">
                  <span className="as-quiet">{`Give this device to ${differs.name}? It is written into its history with your name.`}</span>
                  <button type="button" className="btn btn-primary btn-small" onClick={() => makeHolder(differs.userId)}>
                    Yes, make them the holder
                  </button>
                  <button type="button" className="btn btn-plain btn-small" onClick={() => setConfirmingHolder(false)}>Cancel</button>
                </span>
              )}
            </div>
          )}

          <nav className="as-tabs" aria-label="Device sections">
            {tabs.map((t) => {
              const n = count(t.key);
              return (
                <NavLink key={t.key} end={!t.path} to={t.path ? `${base}/${t.path}` : base}
                  className={({ isActive }) => `as-tab-link${isActive ? ' is-active' : ''}`} data-tab={t.key}>
                  {t.label}
                  {typeof n === 'number' ? <span className="as-tab-count">{n}</span> : null}
                </NavLink>
              );
            })}
          </nav>
        </div>
      </header>
      {compact && spare > 0 && <div className="as-head-spare" aria-hidden="true" style={{ height: spare }} />}

      <div className="as-wrap">
        {problem && <p className="as-error" role="alert">{problem}</p>}
        {write && !portal && <PluggedNotice asset={a} items={data.children} onDone={load} />}
        <main className="as-main" id="as-main">
          <Outlet context={ctx} />
        </main>
      </div>

      {doing === 'edit' && (
        <AssetForm meta={meta} asset={a} holder={wantEdit ? search.get('holder') : null}
          onClose={() => setDoing(null)} onSaved={done} />
      )}
      {doing === 'make-desk' && (
        <MakeDeskDialog asset={a} items={data.children || []} meta={meta} onClose={() => setDoing(null)}
          onDone={(r) => { setDoing(null); navigate(`/assets/desks/${r.desk.id}`); }} />
      )}
      {doing === 'put-on-desk' && (
        <PutOnDeskDialog asset={a} meta={meta} onClose={() => setDoing(null)} onDone={done} />
      )}
      {doing && !['edit', 'make-desk', 'put-on-desk'].includes(doing) && (
        <ActionDialog kind={doing} asset={a} meta={meta} onClose={() => setDoing(null)} onDone={done} />
      )}
      {confirmDialog}
    </div>
  );
}

/**
 * The More menu: a button and a list of actions, arrow keys to move, Escape
 * to close - the ticket page's, in the register's clothes. On a phone it is
 * a sheet along the foot of the screen (AssetLayout.css), and it carries
 * Give to and Take back as well, since the row has no room for them there.
 */
function MoreMenu({ items, open, setOpen }) {
  const wrap = useRef(null);
  const list = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const first = list.current?.querySelector('[role="menuitem"]');
    first?.focus();
    const away = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open, setOpen]);
  const onKey = (e) => {
    const els = [...(list.current?.querySelectorAll('[role="menuitem"]') || [])];
    const at = els.indexOf(document.activeElement);
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setOpen(false); wrap.current?.querySelector('.as-more')?.focus(); }
    if (e.key === 'ArrowDown') { e.preventDefault(); els[(at + 1) % els.length]?.focus(); }
    if (e.key === 'ArrowUp') { e.preventDefault(); els[(at - 1 + els.length) % els.length]?.focus(); }
    if (e.key === 'Tab') setOpen(false);
  };
  return (
    <div className="as-menu-wrap" ref={wrap}>
      <button type="button" className="btn btn-secondary as-more" aria-haspopup="menu" aria-expanded={open}
        aria-label="More actions" onClick={() => setOpen(!open)}>
        <span className="as-more-word">More</span>
        <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" fill="currentColor">
          <circle cx="3" cy="8" r="1.5" /><circle cx="8" cy="8" r="1.5" /><circle cx="13" cy="8" r="1.5" />
        </svg>
      </button>
      {open && (
        <>
          <div className="as-sheet-scrim" aria-hidden="true" onClick={() => setOpen(false)} />
          <ul className="as-menu" role="menu" ref={list} onKeyDown={onKey}>
            {items.map((it) => (
              <li key={it.key} role="none" className={it.phone ? 'as-menu-phone' : undefined}>
                <button type="button" role="menuitem" className={`as-menu-item${it.danger ? ' as-menu-danger' : ''}`} data-action={it.key}
                  onClick={() => { setOpen(false); it.run(); }}>
                  <span>{it.label}</span>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
